import { invert } from "lodash";
import flatten from "lodash/flatten";

import {
  BUYER,
  DEPLOYMENT,
  LIVESTOCK_AGENT,
  TRANSPORTER,
  VENDOR,
} from "constants/businesses";

import { AgGridTables } from "./aggrid";

export const Settings = {
  agencyId: "agencyId",
  allSalesLoaded: "allSalesLoaded",
  auctionScreenMode: "auctionScreenMode",
  auctionPenSelectedSaleLotIds: "auctionPenSelectedSaleLotIds",
  billingRunId: "billingRunId",
  billingRuns: "billingRunId",
  businessFormSaveAction: "businessFormSaveAction",
  businessPICSelectorAttrFilters: {
    [VENDOR]: "businessPICSelectorVendor",
    [TRANSPORTER]: "businessPICSelectorTransporter",
    [BUYER]: "businessPICSelectorBuyer",
    [LIVESTOCK_AGENT]: "businessPICSelectorLivestockAgent",
    [DEPLOYMENT]: "businessPICSelectorDeployment",
  },
  buyersOnlyAuctionScreen: "buyersOnlyAuctionScreen",
  consignmentAttachmentTypeSelection: "consignmentAttachmentTypeSelection",
  consignmentGroupBy: "consignmentGroupBy",
  createConsignmentFormSubmit: "createConsignmentFormSubmit",
  dashboardView: "dashboardView",
  failedRequestModalDismissedUntil: "failedRequestModalDismissedUntil",
  multipleTabsModalDismissedUntil: "multipleTabsModalDismissedUntil",
  multipleTabsModalDismissedForSession: "multipleTabsModalDismissedForSession",
  fullScreen: "fullScreen",
  filter: "filter",
  globalSearch: "globalSearch",
  isAutoSelectLotEnabled: "isAutoSelectLotEnabled",
  isFailedRequestsModalShowing: "isFailedRequestsModalShowing",
  lastAddedConsignmentId: "lastAddedConsignmentId",
  livestockQuickCreateValues: "livestockQuickCreateValues",
  markColor: "markColor",
  mtHostSettings: "mtHostSettings",
  parentSaleTab: "parentSaleTab",
  penScanningGroupType: "penScanningGroupType",
  penView: "penView",
  penningMode: "penningMode",
  preferredScanningMode: "preferredScanningMode",
  resizableSections: {
    billingReviewHorizontal: "resizableSections.billingReviewHorizontal",
    billingReviewVertical: "resizableSections.billingReviewVertical",
  },
  round: "round",
  saleLotAttachmentTypeSelection: "saleLotAttachmentTypeSelection",
  saleProgress: "saleProgress",
  saleSearch: "saleSearch",
  saleWatcherDraftingInformationId: "saleWatcherDraftingInformationId",
  saveWeightDefaultAction: "saveWeightDefaultAction",
  savedScanLotDeploymentId: "savedScanLotDeploymentId",
  savedScanLotRoundId: "savedScanLotRoundId",
  scanPenOrdering: "scanPenOrdering",
  showConsignmentStepper: "showConsignmentStepper",
  showDeduplicatedWeighLotScans: "showDeduplicatedWeighLotScans",
  showQuickSellDrafting: "showQuickSellDrafting",
  speciesId: "speciesId",
  stickySale: "stickySale",
  paymentModalAddAction: "paymentModalAddAction",
  paymentModalUpdateAction: "paymentModalUpdateAction",
  storedSaleLotFormData: "storedSaleLotFormData",
  studWatcher: "studWatcher",
  suggestBuyerWayProperties: "suggestBuyerWayProperties",
  tableViews: "tableViews",
  useQuickBuyerInput: "useQuickBuyerInput",
  watcherViewMode: "watcherViewMode",
  weighbridgeKeyboardMode: "weighbridgeKeyboardMode",
  weighLotScaleName: "weighLotScaleName",
  lastCreatedPenNumber: "lastCreatedPenNumber",
  attachmentSearch: "attachmentSearch",
  selectedAttachmentId: "selectedAttachmentId",
  loadsFilter: "loadsFilter",
  ...invert(AgGridTables),
};

export const allowedSettings = [
  ...Object.keys(Settings),
  ...flatten(
    Object.values(Settings)
      .filter(v => typeof v === "object")
      .map(s => Object.values(s)),
  ),
];

// Finance Settings

export const HeaderLayoutOptions = [
  // Corresponds to agrinous.constants.py:HeaderLayoutOptions
  {
    label: "Simple Agency 1",
    value: "Simple Agency 1",
    description:
      "A basic agency template with Addressee, Logo and Document Information, in that order.",
  },
  {
    label: "Simple Agency 2",
    value: "Simple Agency 2",
    description:
      "A basic agency template with Document Information, Addressee and Logo, in that order.",
  },
  {
    label: "Simple Agency 3",
    value: "Simple Agency 3",
    description:
      "A basic agency template with Logo, Addressee and Document Information, in that order.",
  },
  {
    label: "Simple Agency 4",
    value: "Simple Agency 4",
    description:
      "A basic agency template with To business, Document Information, Logo, From business, in that order.",
  },
  {
    label: "Envelope",
    value: "Envelope",
    description: "A header template that can folded to fit in a DL Envelope.",
  },
  {
    label: "Envelope v2",
    value: "Envelope v2",
    description:
      "A second version header template that can folded to fit in a DL Envelope.",
  },
];

export const BaseFooter = {
  label: "Base Footer",
  value: "BASE",
  description: "A default footer that contains general information.",
};

export const BaseFooterV2 = {
  label: "Base Footer V 2",
  value: "BASE_V_2",
  description: "A grey agrinous logo footer with payment information",
};

export const RemittanceFooter = {
  label: "Remittance Footer",
  value: "REMITTANCE",
  description:
    "A footer that contains a tear off section with payment instructions.",
};

export const RemittanceFooterwithPayment = {
  label: "Remittance Footer with Payment",
  value: "REMITTANCE_WITH_PAYMENT",
  description:
    "A footer that contains a tear off section with payment instructions and payment recording.",
};

export const InvoiceFooterOptions = [
  BaseFooter,
  BaseFooterV2,
  RemittanceFooter,
  RemittanceFooterwithPayment,
];

export const RCTIFooterOptions = [BaseFooter, BaseFooterV2, RemittanceFooter];

export const BusinessLabelSendModeOptions = [
  // Corresponds to agrinous.constants.py:BusinessLabelSendMode
  {
    label: "None",
    value: "NONE",
    description: "Do not send any Business Labels to Xero.",
  },
  {
    label: "All",
    value: "ALL",
    description: "Send every Business Label on a business to Xero.",
  },
  {
    label: "Finance Only",
    value: "FINANCE_ONLY",
    description: 'Only send Business Labels whose Base is "Finance" to Xero.',
  },
];

export const FinanceSubModalSection = {
  GENERAL: "GENERAL",
  INSURANCE: "INSURANCE",
  INTEREST: "INTEREST",
  PAYMENTS: "PAYMENTS",
  PAYMENT_BANKING_DETAILS: "PAYMENT_BANKING_DETAILS",
  RCTI: "RCTI",
  RETAINED_PROCEEDS: "RETAINED_PROCEEDS",
  TAX_INVOICE: "TAX_INVOICE",
  TRADING_TERMS: "TRADING_TERMS",
  TRANSACTIONS: "TRANSACTIONS",
  VENDOR_COMMISSION: "VENDOR_COMMISSION",
  VENDOR_SPLITS: "VENDOR_SPLITS",
  BUYER_SPLITS: "BUYER_SPLITS",
  PAYMENT_PREFERENCES: "PAYMENT_PREFERENCES",
  TRACKING_CATEGORIES: "TRACKING_CATEGORIES",
  SELL_VIA_EX_AGENT_REBATES: "SELL_VIA_EX_AGENT_REBATES",
  XERO_LABEL_SYNC: "XERO_LABEL_SYNC",
};

export enum SettingsTab {
  breeds = "Breeds",
  sexes = "Sexes",
  ages = "Ages",
  products = "Products",
  labels = "Labels",
  marks = "Marks",
  integrations = "Integrations",
  billingRules = "Billing Rules",
  agency = "Agency",
  system = "System",
  nlis = "NLIS",
  users = "Users",
}
